import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { HandCoins } from 'lucide-react';
import api from '@/lib/api';

const cashierHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('cashier_token')}` });
const newRef = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// Cash handover from the drawer to a person who holds it for the branch (custody). Shown only when the company flag is on.
export function ShiftCashHandover({ shift, onDone }) {
  const [enabled, setEnabled] = useState(false);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ holder_employee_id: '', amount: '', notes: '' });
  const [ref, setRef] = useState(newRef());
  const [busy, setBusy] = useState(false);
  const [counted, setCounted] = useState('');

  useEffect(() => {
    api.get('/cash-custody/enabled', { headers: cashierHeaders() }).then(({ data }) => {
      setEnabled(!!data?.enabled);
      if (data?.enabled) api.get('/cash-custody/recipients', { headers: cashierHeaders() }).then((r) => setPeople(r.data || [])).catch(() => {});
    }).catch(() => setEnabled(false));
  }, []);

  if (!enabled) return null;
  const available = Number(shift?.expected_cash || 0);
  const items = shift?.handover?.items || [];

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!form.holder_employee_id || !(amount > 0)) { toast.error('Pick the recipient and amount'); return; }
    if (amount > available + 0.001) { toast.error('More than the drawer holds'); return; }
    setBusy(true);
    try {
      await api.post('/cash-custody', { source: 'drawer', ...form, amount, client_ref: ref }, { headers: cashierHeaders() });
      toast.success('Cash handed over — waiting for the recipient to confirm');
      setForm({ holder_employee_id: '', amount: '', notes: '' }); setRef(newRef()); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not saved'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50/60 p-2 space-y-1.5" data-testid="shift-cash-handover">
      <div className="flex items-center gap-1 text-xs font-semibold text-amber-900"><HandCoins size={13} /> Hand over cash (stays branch cash)</div>
      <div className="grid grid-cols-3 gap-1 text-[11px]" data-testid="handover-drawer-summary">
        <span>In drawer now: <b data-testid="handover-available">{available.toFixed(2)}</b></span>
        <span>Handed over: <b>{Number(shift?.handover?.handed_over || 0).toFixed(2)}</b></span>
        <span>Unconfirmed: <b>{Number(shift?.handover?.in_transit || 0).toFixed(2)}</b></span>
      </div>
      <div className="flex items-center gap-1 text-[11px]">
        <Input className="h-6 w-28 text-[11px]" type="number" placeholder="Counted (optional)" value={counted}
          onChange={(e) => setCounted(e.target.value)} data-testid="handover-counted" />
        {counted !== '' && (
          <span className={Math.abs(parseFloat(counted) - available) < 0.01 ? 'text-success' : 'text-error'} data-testid="handover-count-diff">
            Difference {(parseFloat(counted || 0) - available).toFixed(2)} (shown only — settled at shift close)
          </span>
        )}
      </div>
      <div className="flex gap-1">
        <select className="border rounded px-1 text-xs flex-1 min-w-0 bg-white" value={form.holder_employee_id}
          onChange={(e) => setForm({ ...form, holder_employee_id: e.target.value })} data-testid="handover-recipient">
          <option value="">Recipient…</option>
          {people.map((p) => <option key={p.id} value={p.id}>{p.name}{p.has_login ? '' : ' (no login)'}</option>)}
        </select>
        <Input className="h-7 w-24 text-xs" type="number" placeholder="Amount" value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="handover-amount" />
        <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={submit} data-testid="handover-submit">Hand over</Button>
      </div>
      <Input className="h-7 text-xs" placeholder="Note (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="handover-note" />
      {items.length > 0 && (
        <div className="space-y-0.5" data-testid="handover-list">
          {items.map((h) => (
            <div key={h.id} className="flex justify-between text-[11px]" data-testid={`handover-item-${h.id}`}>
              <span>{h.holder_name} · {Number(h.amount).toFixed(2)}</span>
              <Badge variant="outline" className="h-4 text-[10px] capitalize">{String(h.status).replace('_', ' ')}</Badge>
            </div>
          ))}
          {shift.handover.in_transit > 0 && <div className="text-[11px] text-error" data-testid="handover-in-transit">Not yet confirmed: {Number(shift.handover.in_transit).toFixed(2)}</div>}
        </div>
      )}
    </div>
  );
}
